import * as z from 'zod/mini';

export const OrderLineSchema = z.object({
  id: z.number(),
  order_block_id: z.number(),
  order_session_room_type_id: z.nullish(z.number()),
  group_title: z.string(),
  unit_type: z.string(),
  name: z.string(),
  // price: z.object({
  //   amount: z.number(),
  //   unit: z.string(),
  // }),
  quantities: z.array(z.number()),
  meta_data: z.nullish(z.record(z.string(), z.string())),

  // These are values that are added dynamically by order line rules
  // Should the line be visible?
  visible: z.nullish(z.boolean()),
  // Should the line be visible, but disabled?
  disabled: z.nullish(z.boolean()),
  // Should the line have a recommended badge?
  recommended: z.nullish(z.boolean()),
  // Should the line be forced to true/checked/selected?
  forced: z.nullish(z.boolean()),
  // Should the line have a quantity selection for each pax?
  per_pax: z.nullish(z.boolean()),
  // Which paxes are disabled for this order line?
  pax_disabled: z.nullish(z.array(z.boolean())),
});

export const OrderBlockSchema = z.object({
  id: z.number(),
  name: z.string(),
  start_date: z.nullish(z.string()),
  end_date: z.nullish(z.string()),
  package_id: z.number(),
  display_type: z.string(),
});

export const SummaryLineSchema = z.object({
  type: z.string(),
  name: z.string(),
  description: z.nullish(z.string()),
  price: z.number(),
  order_block_id: z.nullish(z.number()),
  items: z.nullish(z.array(z.object({
    name: z.string(),
    description: z.nullish(z.string()),
    price: z.number(),
  }))),
});

const ExtraDaySchema = z.object({
  order_block_id: z.number(),
  name: z.string(),
  min: z.number(),
  max: z.number(),
  quantity: z.number(),
});

export const SummarySchema = z.object({
  title: z.string(),
  pax: z.number(),
  start_date: z.string(),
  end_date: z.string(),
  nights: z.number(),
  lines: z.array(SummaryLineSchema),
  total: z.number(),
});

const RoomTypeSchema = z.object({
  service_code: z.string(),
  name: z.string(),
  pax: z.number(),
});

const RoomTypeSelectedSchema = z.object({
  order_session_room_type_id: z.number(),
  service_code: z.string(),
  pax: z.number(),
});

export const OrderLineRuleSchema = z.object({
  id: z.string(),
  name: z.string(),
  rule_type: z.enum(['prerequisite', 'do_not_show', 'always_together', 'recommendation']),
  source_order_lines: z.array(z.number()),
  target_order_lines: z.array(z.number()),
});

export const OrderLineGroupSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.enum(['mutually_exclusive', 'mutually_exclusive_mandatory', 'additions_per_pax']),
  order_lines: z.array(z.number()),
});

export const PackageSessionStaySchema = z.object({
  package_id: z.number(),
  name: z.string(),
  // description: z.string(),
  start_date: z.string(),
  end_date: z.string(),
  selected: z.boolean(),
});

export const NotificationSchema = z.object({
  type: z.enum(['error', 'warning', 'info']),
  message: z.string(),
});

// Custom fields can be an object, but is an array if its empty (Laravel JSON encoding)
export const CustomFieldSchema = z.union([
  z.object({}),
  z.array(z.any()),
]);

export const PackageSessionStoreRequestSchema = z.object({
  package_id: z.number(),
  start_date: z.string(),
  pax: z.number(),
  locale: z.string(),
  office_id: z.number(),
  department_id: z.number(),

  // If we've a session ID, we send it, otherwise the backend should
  // initiate a new session.
  session_id: z.nullish(z.string()),

  // The package session request can send all fields it got from the
  // response. But those are optional, since our first request only
  // contains the field above.
  end_date: z.nullish(z.string()),
  order_blocks: z.nullish(z.array(OrderBlockSchema)),
  order_lines: z.nullish(z.array(OrderLineSchema)),
  default_order_lines: z.nullish(z.array(OrderLineSchema)),
  order_line_rules: z.nullish(z.array(OrderLineRuleSchema)),
  order_line_groups: z.nullish(z.array(OrderLineGroupSchema)),
  extra_days: z.nullish(z.array(ExtraDaySchema)),
  room_types: z.nullish(z.array(RoomTypeSchema)),
  room_types_selected: z.nullish(z.array(RoomTypeSelectedSchema)),
  summary: z.nullish(SummarySchema),
  prestays: z.nullish(z.array(PackageSessionStaySchema)),
  poststays: z.nullish(z.array(PackageSessionStaySchema)),
  custom_fields: z.nullish(CustomFieldSchema),
  notifications: z.nullish(z.array(NotificationSchema)),
  debug: z.nullish(z.array(z.any())),
});

export const PackageSessionStoreResponseSchema = z.object({
  session_id: z.string(),
});

export const PackageSessionResponseSchema = z.object({
  package_id: z.number(),
  session_id: z.string(),
  start_date: z.string(),
  end_date: z.string(),
  pax: z.number(),
  locale: z.string(),
  office_id: z.number(),
  department_id: z.number(),
  order_blocks: z.array(OrderBlockSchema),
  order_lines: z.array(OrderLineSchema),
  default_order_lines: z.array(OrderLineSchema),
  order_line_rules: z.nullish(z.array(OrderLineRuleSchema)),
  order_line_groups: z.nullish(z.array(OrderLineGroupSchema)),
  extra_days: z.array(ExtraDaySchema),
  room_types: z.array(RoomTypeSchema),
  room_types_selected: z.array(RoomTypeSelectedSchema),
  summary: SummarySchema,
  prestays: z.array(PackageSessionStaySchema),
  poststays: z.array(PackageSessionStaySchema),
  custom_fields: CustomFieldSchema,
  notifications: z.array(NotificationSchema),
  debug: z.nullish(z.array(z.any())),
});
